import { IState } from "@/apps/credit-line/models/IState";
import {
  ICardInfo,
  ICreditLineDetails,
  IPaymentSchedule,
  ITransaction,
  IWithdrawal,
  ServerRequestableActions,
} from "../models/CreditLineDetails";
import { IOffer } from "@/apps/credit-line/models/IOffer";
import TransactionFilter from "@/apps/credit-line/types/TransactionFilter";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { ReportLostOrDamagedCard } from "../types/cards";
import {
  AreValidationsRequiredResponse,
  NationalIdValidationResponse,
} from "@/apps/credit-line/models/IKycValidations";
import { RequireAmlSurveyResponse } from "@/apps/credit-line/models/IAmlSurvey";
import { WithdrawMethod } from "@/apps/credit-line/types/withdrawMethod";
import {
  IWithdrawalEstimate,
  IWithdrawalEstimateField,
} from "@/apps/credit-line/models/IWithdrawalEstimate";
import { TransactionViewState } from "@/apps/credit-line/types/TransactionViewState";

/**
 * MUTATIONS
 */
const mutations = {
  SET_PAYMENT_SCHEDULE: (
    state: IState,
    payload: Array<IPaymentSchedule> | null
  ): void => {
    state.paymentSchedule = payload;
  },
  SET_PENDING_WITHDRAWAL: (
    state: IState,
    payload: IWithdrawal | null
  ): void => {
    state.pendingWithdraw = payload;
  },
  SET_WITHDRAWALS: (state: IState, payload: IWithdrawal[] | null): void => {
    state.withdrawals = payload;
  },
  SET_CURRENCY: (state: IState, payload: string | null): void => {
    state.currency = payload;
  },
  SET_OFFER: (state: IState, payload: IOffer | null): void => {
    state.offer = payload;
  },
  SET_OFFER_UNAVAILABLE: (state: IState, payload: boolean): void => {
    state.offer_unavailable = payload;
  },
  SET_TRANSACTIONS_FILTERS: (
    state: IState,
    payload: TransactionFilter[]
  ): void => {
    state.transactionsFilters = payload;
  },
  SET_TRANSACTIONS_LIST: (state: IState, payload: ITransaction[]): void => {
    state.transactionList = payload;
  },
  SET_SELECTED_WITHDRAW_METHOD: (
    state: IState,
    payload: WithdrawMethod | null
  ): void => {
    state.selectedWithdrawMethod = payload;
  },
  SET_WITHDRAWAL_ESTIMATE: (
    state: IState,
    payload: IWithdrawalEstimate | null
  ): void => {
    state.withdrawalEstimate = payload;
  },
  SET_WITHDRAWAL_ESTIMATE_FIELDS: (
    state: IState,
    payload: IWithdrawalEstimateField[] | null
  ): void => {
    state.withdrawalEstimateFields = payload;
  },
  // Credit card
  INIT_SUMMARY: (state: IState, summary: ICreditLineDetails): void => {
    state.summary = summary;
  },
  ADD_CREDIT_CARD: (state: IState, cardInfo: ICardInfo): void => {
    if (state.summary) {
      state.summary.cards = [...(state.summary?.cards ?? []), cardInfo];
    }
  },
  REMOVE_CREDIT_CARD: (state: IState, lastFour: string): void => {
    if (state.summary) {
      state.summary.cards = [
        ...(state.summary?.cards ?? []).filter(
          (card) => card.lastFour !== lastFour
        ),
      ];
    }
  },
  SET_CARD_STATUS: (
    state: IState,
    { lastFour, status }: { lastFour: string; status: PayJoyCardStatus }
  ): void => {
    if (state.summary?.cards) {
      const cardIndex = state.summary.cards.findIndex(
        (card) => card.lastFour === lastFour
      );

      if (cardIndex !== -1) {
        state.summary.cards[cardIndex].status = status;
      }
    }
  },
  SET_CREATING_VIRTUAL_CARD: (state: IState, creating: boolean): void => {
    state.runningActions.createVirtualCreditCard = creating;
  },
  CLEAR_SERVER_REQUESTED_ACTION: (
    state: IState,
    action: ServerRequestableActions
  ): void => {
    const remainingActions = state.summary?.serverRequestedActions?.filter(
      (a) => a === action
    );

    state.summary = {
      ...state.summary,
      serverRequestedActions: remainingActions ?? [],
    } as ICreditLineDetails;
  },
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  ADD_SHIPMENT_LOG: (state: IState, message: Record<string, any>): void => {
    if (state.shipmentLogs) {
      state.shipmentLogs.push({
        date: new Date().toLocaleString(),
        message,
      });
    }
  },
  CLEAR_SHIPMENT_LOGS: (state: IState): void => {
    state.shipmentLogs = [];
  },
  SET_CARD_LOST_OR_DAMAGED: (
    state: IState,
    payload: ReportLostOrDamagedCard
  ): void => {
    state.reportLostOrDamagedCard = payload;
  },
  SET_WITHDRAWAL_VALIDATION: (
    state: IState,
    payload: AreValidationsRequiredResponse
  ): void => {
    state.withdrawalValidation = payload;
  },
  SET_AML_SURVEY_DATA: (
    state: IState,
    payload: RequireAmlSurveyResponse
  ): void => {
    state.amlSurveyData = payload;
  },
  SET_CURP_VALIDATION_RESULT: (
    state: IState,
    curpValidationResult: NationalIdValidationResponse
  ): void => {
    state.curpValidationResult = curpValidationResult;
  },
  SET_VALIDATION_SUCCESS: (state: IState, validationSuccess: boolean): void => {
    state.validationSuccess = validationSuccess;
  },
  SET_CURRENT_TRANSACTION_VIEW_STATE: (
    state: IState,
    payload: TransactionViewState
  ): void => {
    state.currentTransactionViewState = payload;
  },
  SET_SELECTED_TRANSACTION: (
    state: IState,
    payload: ITransaction | null
  ): void => {
    state.selectedTransaction = payload;
  },
  RESET_TRANSACTION_VIEW: (state: IState): void => {
    state.currentTransactionViewState = TransactionViewState.LIST;
    state.selectedTransaction = null;
  },
};

export default mutations;
